import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonIcon from '@mui/icons-material/Person';
import HeightIcon from '@mui/icons-material/Height';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { clientProfileHeader as c } from '@/styles/tokens';
import { useAppDispatch } from '@/store';
import { createConversation } from '@/store/chat/chatSlice';
import { toastService } from '@services/toast.service';
import { useAuth } from '@/hooks/useAuth';
import { CreateAppointmentByExpertDialog } from '@components/appointments/expert/CreateAppointmentByExpertDialog';

export interface ClientProfileHeaderProps {
  clientId: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  avatar: string | null;
  age: number | null;
  height: number | null;
  weight: number | null;
}

const buttonSx = {
  height: 40,
  px: 2,
  borderRadius: '6px',
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  '& .MuiButton-startIcon': { mr: '5px', '& svg': { fontSize: 16 } },
} as const;

// Decimal values may come from the API as strings.
const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Empty value: the row stays with a dash, so a missing contact is visible.
const ContactRow: React.FC<{ icon: React.ReactNode; value: string | null }> = ({ icon, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon}
    <Typography sx={{ fontSize: 16, lineHeight: '19px', color: value ? c.text : c.label }}>{value || '—'}</Typography>
  </Box>
);

const InfoBadge: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  // Narrow screens: tighter padding so three badges and the profile button fit one row.
  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, height: 52, px: { xs: 1, sm: 1.5 }, py: '5px', bgcolor: c.badgeBg, borderRadius: '8px' }}>
    {icon}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontSize: 12, lineHeight: '14px', color: c.label }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, lineHeight: '16px', fontWeight: 500, color: c.text, whiteSpace: 'nowrap' }}>{value}</Typography>
    </Box>
  </Box>
);

/** Expert's client page header (Figma "Profile-Header"). "Write" opens the chat with the client, "Schedule" - the new meeting dialog. */
export const ClientProfileHeader: React.FC<ClientProfileHeaderProps> = ({
  clientId, fullName, email, phone, avatar, age, height, weight,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.header' });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openingChat, setOpeningChat] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const { user } = useAuth();

  // Existing conversation with the client or a new one (the API returns either).
  const openChat = async () => {
    setOpeningChat(true);
    try {
      const { id } = await dispatch(createConversation({ targetUserId: Number(clientId) })).unwrap();
      navigate(`/chat/${id}`);
    } catch {
      toastService.error(t('writeError'));
      setOpeningChat(false);
    }
  };
  const iconSx = { fontSize: 20, color: c.icon };
  const ageValue = toNumber(age);
  const heightValue = toNumber(height);
  const weightValue = toNumber(weight);
  const name = fullName || t('clientFallback');
  // Stable reference: the dialog resets its state when the preselected client changes.
  const meetingClient = useMemo(() => ({ id: Number(clientId), name, avatar: avatar ?? undefined }), [clientId, name, avatar]);

  return (
    <Box
      data-i-doc={SectionIndices.EXPERT_CLIENT_PROFILE_HEADER}
      // Edge to edge between the sidebars: cancels the dashboard tab side padding (20).
      sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${c.divider}`, pt: 4, px: 2.5, pb: 2.5, mx: -2.5, mb: 4 }}
    >
      {/* Grid areas: desktop - avatar | contacts over buttons | badges on the right; mobile - avatar + contacts, then badges,
          then full-width buttons (Figma mobile "Profile-Header"). */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '80px minmax(0, 1fr)', md: '80px auto minmax(0, 1fr)' },
          gridTemplateAreas: {
            xs: '"avatar contacts" "badges badges" "buttons buttons"',
            md: '"avatar contacts badges" "avatar buttons badges"',
          },
          columnGap: 2.5,
          rowGap: 2.5,
          alignItems: 'start',
        }}
      >
        <UserAvatar path={avatar} name={name} alt={name} sx={{ gridArea: 'avatar', width: 80, height: 80 }} />

        <Box sx={{ gridArea: 'contacts', display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, lineHeight: '26px', fontWeight: 600, color: c.name }}>{name}</Typography>
          {email && <ContactRow icon={<EmailIcon sx={iconSx} />} value={email} />}
          <ContactRow icon={<PhoneIcon sx={iconSx} />} value={phone} />
        </Box>

        <Box sx={{ gridArea: 'buttons', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: { xs: 'nowrap', md: 'wrap' } }}>
          <Button
            variant="contained"
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={openChat}
            disabled={openingChat}
            sx={{ ...buttonSx, flex: { xs: 1, md: 'none' }, bgcolor: c.primary, color: c.primaryText, '&:hover': { bgcolor: c.primary, boxShadow: 'none', filter: 'brightness(0.95)' } }}
          >
            {t('write')}
          </Button>
          <Button
            startIcon={<CalendarTodayOutlinedIcon />}
            onClick={() => setMeetingOpen(true)}
            disabled={!user}
            sx={{ ...buttonSx, flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap', bgcolor: c.secondaryBg, color: c.secondaryText, '&:hover': { bgcolor: c.secondaryBg, filter: 'brightness(0.95)' } }}
          >
            {t('schedule')}
          </Button>
        </Box>

        <Box
          sx={{
            gridArea: 'badges', display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: { xs: 'nowrap', md: 'wrap' },
            justifySelf: { xs: 'start', md: 'end' }, pl: { xs: 0, md: 5 },
          }}
        >
          {ageValue !== null && <InfoBadge icon={<PersonIcon sx={iconSx} />} label={t('age')} value={t('ageValue', { value: ageValue })} />}
          {heightValue !== null && (
            <InfoBadge icon={<HeightIcon sx={iconSx} />} label={t('height')} value={t('heightValue', { value: heightValue })} />
          )}
          {weightValue !== null && (
            <InfoBadge icon={<MonitorWeightIcon sx={iconSx} />} label={t('weight')} value={t('weightValue', { value: weightValue.toFixed(2) })} />
          )}
          <Tooltip title={t('openProfile')}>
            <IconButton
              aria-label={t('openProfile')}
              onClick={() => navigate(`/client/${clientId}/profile`)}
              sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '6px', bgcolor: c.iconButtonBg, '&:hover': { bgcolor: c.iconButtonBg, filter: 'brightness(0.95)' } }}
            >
              <OpenInNewIcon sx={{ fontSize: 16, color: c.iconButtonIcon }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {user && (
        <CreateAppointmentByExpertDialog
          open={meetingOpen}
          onClose={() => setMeetingOpen(false)}
          onSuccess={() => setMeetingOpen(false)}
          expertId={user.id}
          clients={[meetingClient]}
          preselectedClient={meetingClient}
        />
      )}
    </Box>
  );
};
